import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Result,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, ReloadOutlined, ThunderboltOutlined } from "../components/icons";
import { trpc } from "../trpc";

const { Text, Title } = Typography;

type AnyRecord = Record<string, any>;

function formatTime(value: string | Date | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleString("zh-CN");
}

function todayDateString() {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  return `${year}-${month}-${day}`;
}

function accrualStatusTag(status: string) {
  const map: Record<string, { color: string; label: string }> = {
    skipped: { color: "default", label: "跳过" },
    pending_sync: { color: "processing", label: "待同步" },
    held: { color: "default", label: "已挂起" },
    processing: { color: "warning", label: "同步中" },
    synced: { color: "success", label: "已同步" },
    sync_unconfirmed: { color: "warning", label: "待复查" },
    sync_failed: { color: "warning", label: "待复查" },
  };
  const item = map[status] || { color: "default", label: status };
  return <Tag color={item.color}>{item.label}</Tag>;
}

function skipReasonLabel(reason?: string) {
  const map: Record<string, string> = {
    ineligible_type: "类型不符",
    no_guzi_hub: "无 Guzi hub",
    no_taoke_id: "无淘客 ID",
    daily_cap_reached: "已达日上限",
  };
  return reason ? map[reason] || reason : "-";
}

function batchStatusTag(status: string) {
  const map: Record<string, { color: string; label: string }> = {
    processing: { color: "processing", label: "处理中" },
    held: { color: "default", label: "已挂起" },
    success: { color: "success", label: "成功" },
    partial: { color: "warning", label: "部分成功" },
    failed: { color: "error", label: "失败" },
  };
  const item = map[status] || { color: "default", label: status };
  return <Tag color={item.color}>{item.label}</Tag>;
}

function verificationOutcomeTag(outcome: string) {
  const map: Record<string, { color: string; label: string }> = {
    confirmed: { color: "success", label: "确认成功" },
    rejected: { color: "error", label: "确认失败" },
    skipped: { color: "default", label: "已跳过" },
    error: { color: "warning", label: "查询异常" },
  };
  const item = map[outcome] || { color: "default", label: outcome };
  return <Tag color={item.color}>{item.label}</Tag>;
}

function verificationActionLabel(action: string) {
  const map: Record<string, string> = {
    mark_synced: "标记已同步",
    reset_pending_sync: "重置待同步并重发",
    none: "无状态变更",
  };
  return map[action] || action;
}

interface AdjustmentFormValues {
  tenantId: string;
  taokeId: string;
  accrualDate: string;
  points: number;
  reason: string;
}

function AccrualsTab() {
  const { message } = App.useApp();
  const [items, setItems] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [syncDetail, setSyncDetail] = useState<AnyRecord | null>(null);
  const [syncDetailLoading, setSyncDetailLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    taokeId: "",
    accrualDate: "",
    status: "all" as string,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await trpc.speechPoint.accruals.list.query({
        page: filters.page,
        limit: filters.limit,
        taokeId: filters.taokeId || undefined,
        accrualDate: filters.accrualDate || undefined,
        status: filters.status === "all" ? undefined : (filters.status as any),
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      message.error(error?.message || "加载加分明细失败");
    } finally {
      setLoading(false);
    }
  }, [filters, message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openSyncDetail = async (record: AnyRecord) => {
    const batchId = record.syncInfo?.batchId || record.syncBatchId;
    if (!batchId) return;

    setSyncDetailLoading(true);
    setSyncDetail({ record });
    try {
      const batch = await trpc.speechPoint.syncBatches.get.query({
        id: String(batchId),
        taokeId: record.taokeId || undefined,
      });
      setSyncDetail({ record, batch });
    } catch (error: any) {
      message.error(error?.message || "加载同步批次详情失败");
      setSyncDetail(null);
    } finally {
      setSyncDetailLoading(false);
    }
  };

  const columns: ColumnsType<AnyRecord> = [
    { title: "消息时间", dataIndex: "messageTime", width: 170, render: formatTime },
    { title: "计分日期", dataIndex: "accrualDate", width: 110 },
    {
      title: "淘客 ID",
      dataIndex: "taokeId",
      width: 160,
      render: (value) => (value ? <Text copyable>{value}</Text> : "-"),
    },
    {
      title: "发送人",
      dataIndex: "senderName",
      width: 140,
      render: (_, record) => record.senderName || record.senderId || "-",
    },
    { title: "类型", dataIndex: "msgtype", width: 80, render: (v) => <Tag>{v}</Tag> },
    { title: "规则分", dataIndex: "basePoints", width: 80 },
    { title: "实得分", dataIndex: "awardedPoints", width: 80 },
    { title: "状态", dataIndex: "status", width: 100, render: accrualStatusTag },
    {
      title: "同步批次",
      key: "syncBatch",
      width: 120,
      render: (_, record) => {
        const batchId = record.syncInfo?.batchId || record.syncBatchId;
        if (!batchId) return "-";
        const chaotaoOk = record.syncInfo?.chaotaoResult?.success;
        return (
          <Space direction="vertical" size={0}>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => void openSyncDetail(record)}>
              查看
            </Button>
            {record.syncInfo?.chaotaoResult && (
              <Tag color={chaotaoOk ? "success" : "error"} style={{ margin: 0 }}>
                {chaotaoOk ? "超淘成功" : "超淘失败"}
              </Tag>
            )}
          </Space>
        );
      },
    },
    { title: "跳过原因", dataIndex: "skipReason", width: 110, render: skipReasonLabel },
    {
      title: "msgid",
      dataIndex: "msgid",
      width: 200,
      ellipsis: true,
      render: (value) => <Text copyable={{ text: value }}>{value}</Text>,
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space wrap>
        <Input
          placeholder="淘客 ID"
          style={{ width: 180 }}
          value={filters.taokeId}
          onChange={(e) => setFilters((p) => ({ ...p, taokeId: e.target.value, page: 1 }))}
          allowClear
        />
        <Input
          type="date"
          style={{ width: 160 }}
          value={filters.accrualDate}
          onChange={(e) => setFilters((p) => ({ ...p, accrualDate: e.target.value, page: 1 }))}
        />
        <Select
          style={{ width: 140 }}
          value={filters.status}
          onChange={(value) => setFilters((p) => ({ ...p, status: value, page: 1 }))}
          options={[
            { label: "全部状态", value: "all" },
            { label: "待同步", value: "pending_sync" },
            { label: "已挂起", value: "held" },
            { label: "已同步", value: "synced" },
            { label: "未确认", value: "sync_unconfirmed" },
            { label: "跳过", value: "skipped" },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
          刷新
        </Button>
      </Space>
      <Table
        rowKey={(record) => record._id || record.msgid}
        columns={columns}
        dataSource={items}
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          current: filters.page,
          pageSize: filters.limit,
          total,
          showSizeChanger: true,
          onChange: (page, limit) => setFilters((p) => ({ ...p, page, limit })),
        }}
      />
      <Modal
        title="同步批次详情"
        open={!!syncDetail}
        onCancel={() => setSyncDetail(null)}
        footer={null}
        width={900}
        destroyOnHidden
      >
        {syncDetail && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="批次 ID">
                <Text copyable>{String(syncDetail.batch?._id || syncDetail.record?.syncInfo?.batchId || "")}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="淘客 ID">{syncDetail.record?.taokeId || "-"}</Descriptions.Item>
              <Descriptions.Item label="批次状态">
                {batchStatusTag(syncDetail.batch?.status || syncDetail.record?.syncInfo?.batchStatus || "")}
              </Descriptions.Item>
              <Descriptions.Item label="超淘结果">
                {syncDetail.batch?.chaotaoResult ? (
                  <Tag color={syncDetail.batch.chaotaoResult.success ? "success" : "error"}>
                    {syncDetail.batch.chaotaoResult.success
                      ? "成功"
                      : syncDetail.batch.chaotaoResult.error || "失败"}
                  </Tag>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="同步项状态">
                {syncDetail.batch?.syncItem?.status || syncDetail.record?.syncInfo?.syncItem?.status || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="同步项错误">
                {syncDetail.batch?.syncItem?.errorMessage ||
                  syncDetail.record?.syncInfo?.syncItem?.errorMessage ||
                  "-"}
              </Descriptions.Item>
              <Descriptions.Item label="发放时间">{formatTime(syncDetail.batch?.startedAt)}</Descriptions.Item>
            </Descriptions>
            {(syncDetail.batch?.chaotaoApiCalls || syncDetail.record?.syncInfo?.chaotaoApiCalls || []).map(
              (call: AnyRecord, index: number) => (
                <Card
                  key={index}
                  size="small"
                  title={`超淘 API 调用 #${index + 1} (${call.success ? "成功" : "失败"})`}
                  loading={syncDetailLoading}
                >
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <div>
                      <Text strong>请求</Text>
                      <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 160, marginTop: 8 }}>
                        {JSON.stringify(call.request, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <Text strong>响应</Text>
                      <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 200, marginTop: 8 }}>
                        {JSON.stringify(call.response, null, 2)}
                      </pre>
                    </div>
                  </Space>
                </Card>
              )
            )}
            {!(syncDetail.batch?.chaotaoApiCalls || syncDetail.record?.syncInfo?.chaotaoApiCalls || []).length && (
              <Alert
                type="info"
                showIcon
                message="该批次未记录超淘原始请求/响应（历史批次在功能上线前产生）"
              />
            )}
          </Space>
        )}
      </Modal>
    </Space>
  );
}

function ReconcileTab() {
  const { message } = App.useApp();
  const [form] = Form.useForm<AdjustmentFormValues>();
  const [queryForm] = Form.useForm<{ taokeId: string; accrualDate: string }>();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<AnyRecord | null>(null);
  const [reconcile, setReconcile] = useState<AnyRecord | null>(null);
  const [sources, setSources] = useState<AnyRecord[]>([]);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    trpc.fadan.sources.list
      .query()
      .then(setSources)
      .catch(() => {});
    queryForm.setFieldsValue({ accrualDate: todayDateString() });
  }, [queryForm]);

  const handleQuery = async () => {
    const values = await queryForm.validateFields();
    setLoading(true);
    try {
      const [summaryResult, reconcileResult] = await Promise.all([
        trpc.speechPoint.dailySummary.get.query(values),
        trpc.speechPoint.reconcile.query(values),
      ]);
      setSummary(summaryResult);
      setReconcile(reconcileResult);
    } catch (error: any) {
      message.error(error?.message || "查询失败");
    } finally {
      setLoading(false);
    }
  };

  const openAdjustModal = () => {
    const values = queryForm.getFieldsValue();
    form.setFieldsValue({
      tenantId: sources[0]?.tenantId || "",
      taokeId: values.taokeId || "",
      accrualDate: values.accrualDate || todayDateString(),
      points: undefined,
      reason: "",
    });
    setAdjustModalOpen(true);
  };

  const handleAdjust = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await trpc.speechPoint.adjustments.create.mutate(values);
      message.success("补分已提交，将在下轮同步");
      setAdjustModalOpen(false);
      form.resetFields();
      await handleQuery();
    } catch (error: any) {
      message.error(error?.message || "补分失败");
    } finally {
      setSaving(false);
    }
  };

  const pendingAccrualColumns: ColumnsType<AnyRecord> = [
    { title: "时间", dataIndex: "messageTime", width: 170, render: formatTime },
    { title: "类型", dataIndex: "msgtype", width: 80 },
    { title: "实得分", dataIndex: "awardedPoints", width: 80 },
    { title: "状态", dataIndex: "status", width: 100, render: accrualStatusTag },
    { title: "msgid", dataIndex: "msgid", ellipsis: true },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Form form={queryForm} layout="inline" onFinish={handleQuery}>
        <Form.Item name="taokeId" label="淘客 ID" rules={[{ required: true, message: "请输入淘客 ID" }]}>
          <Input placeholder="taoke_id" style={{ width: 200 }} allowClear />
        </Form.Item>
        <Form.Item name="accrualDate" label="日期" rules={[{ required: true, message: "请选择日期" }]}>
          <Input type="date" style={{ width: 160 }} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              查询核验
            </Button>
            <Button icon={<PlusOutlined />} onClick={openAdjustModal}>
              人工补分
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {summary && (
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6}>
            <Card variant="borderless" className="shadow-sm">
              <Statistic title="应得总分" value={summary.accruedTotal} suffix="分" />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Card variant="borderless" className="shadow-sm">
              <Statistic title="已同步" value={summary.syncedTotal} suffix="分" valueStyle={{ color: "#16a34a" }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Card variant="borderless" className="shadow-sm">
              <Statistic title="待同步/挂起" value={summary.pendingTotal} suffix="分" valueStyle={{ color: "#2563eb" }} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Card variant="borderless" className="shadow-sm">
              <Statistic title="已挂起" value={summary.heldTotal ?? 0} suffix="分" />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Card variant="borderless" className="shadow-sm">
              <Statistic title="未确认" value={summary.unconfirmedTotal ?? summary.failedTotal ?? 0} suffix="分" valueStyle={{ color: "#d97706" }} />
            </Card>
          </Col>
        </Row>
      )}

      {reconcile && (
        <Card
          variant="borderless"
          className="shadow-sm"
          title={
            <Space>
              <span>核验结果</span>
              {reconcile.balanced ? (
                <Tag color="success">账目平衡</Tag>
              ) : (
                <Tag color="error">存在差异</Tag>
              )}
            </Space>
          }
        >
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
            <Descriptions.Item label="跳过条数">{summary?.skippedCount ?? 0}</Descriptions.Item>
            <Descriptions.Item label="补分已同步">{summary?.adjustmentSyncedTotal ?? 0} 分</Descriptions.Item>
            <Descriptions.Item label="补分待同步">{summary?.adjustmentPendingTotal ?? 0} 分</Descriptions.Item>
            <Descriptions.Item label="差额">{reconcile.diff}</Descriptions.Item>
          </Descriptions>

          {(reconcile.pendingAccruals?.length > 0 || reconcile.pendingAdjustments?.length > 0) && (
            <div style={{ marginTop: 16 }}>
              <Title level={5}>待处理明细</Title>
              <Table
                rowKey={(record) => record._id}
                size="small"
                columns={pendingAccrualColumns}
                dataSource={reconcile.pendingAccruals || []}
                pagination={false}
                style={{ marginBottom: 16 }}
              />
              {(reconcile.pendingAdjustments || []).map((item: AnyRecord) => (
                <div key={item._id} style={{ marginBottom: 8 }}>
                  <Tag color="purple">补分</Tag>
                  <Text>
                    {item.points} 分 · {item.reason} · {accrualStatusTag(item.status)}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Modal
        title="人工补分"
        open={adjustModalOpen}
        onCancel={() => setAdjustModalOpen(false)}
        onOk={handleAdjust}
        confirmLoading={saving}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="tenantId" label="Tenant" rules={[{ required: true, message: "请选择 tenant" }]}>
            <Select
              options={sources.map((s) => ({
                label: `${s.name} (${s.tenantId})`,
                value: s.tenantId,
              }))}
              placeholder="选择存档源 tenant"
            />
          </Form.Item>
          <Form.Item name="taokeId" label="淘客 ID" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="accrualDate" label="计分日期" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
          <Form.Item
            name="points"
            label="补分点数"
            rules={[{ required: true, message: "请输入补分点数" }]}
            extra="正数补分，负数冲正"
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="reason" label="原因" rules={[{ required: true, message: "请填写原因" }]}>
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}

function TestAddPointsTab() {
  const { message } = App.useApp();
  const [form] = Form.useForm<{ taokeId: string; points: number }>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnyRecord | null>(null);

  const handleTest = async () => {
    const values = await form.validateFields();
    setLoading(true);
    setResult(null);
    try {
      const response = await trpc.speechPoint.test.addPoints.mutate(values);
      setResult(response);
      if (response.success) {
        message.success("测试录入成功");
      } else {
        message.error(response.error || "测试录入失败");
      }
    } catch (error: any) {
      message.error(error?.message || "请求失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type="warning"
        showIcon
        message="测试录入会立即向超淘发送真实加分请求"
        description="此操作不经过加分明细与同步批次，仅用于验证代理 API 与会员 ID 是否可用。请使用测试账号，避免给真实用户误加分。"
      />
      <Form
        form={form}
        layout="inline"
        initialValues={{ points: 1 }}
        onFinish={handleTest}
      >
        <Form.Item
          name="taokeId"
          label="会员 ID"
          rules={[{ required: true, message: "请输入会员 ID（huiyuanid）" }]}
        >
          <Input placeholder="超淘会员 ID" style={{ width: 220 }} allowClear />
        </Form.Item>
        <Form.Item
          name="points"
          label="积分"
          rules={[{ required: true, message: "请输入积分" }]}
        >
          <InputNumber min={1} max={9999} style={{ width: 120 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            测试录入
          </Button>
        </Form.Item>
      </Form>
      {result && (
        <Card variant="borderless" className="shadow-sm" title="调用结果">
          <Result
            status={result.success ? "success" : "error"}
            title={result.success ? "录入成功" : "录入失败"}
            subTitle={result.error || (result.success ? `已为会员 ${result.taokeId} 录入 ${result.points} 分` : undefined)}
            style={{ padding: "16px 0" }}
          />
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="代理已配置">{result.configured ? "是" : "否"}</Descriptions.Item>
            <Descriptions.Item label="会员 ID">{result.taokeId}</Descriptions.Item>
            <Descriptions.Item label="积分">{result.points}</Descriptions.Item>
          </Descriptions>
          {result.response && (
            <div style={{ marginTop: 16 }}>
              <Text strong>超淘原始响应</Text>
              <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 240, marginTop: 8 }}>
                {JSON.stringify(result.response, null, 2)}
              </pre>
            </div>
          )}
        </Card>
      )}
    </Space>
  );
}

function SyncBatchesTab() {
  const { message } = App.useApp();
  const [items, setItems] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [detail, setDetail] = useState<AnyRecord | null>(null);
  const [latestVerification, setLatestVerification] = useState<AnyRecord | null>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await trpc.speechPoint.syncBatches.list.query({ page, limit });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      message.error(error?.message || "加载同步批次失败");
    } finally {
      setLoading(false);
    }
  }, [page, limit, message]);

  const openDetail = async (record: AnyRecord) => {
    setDetail(record);
    setLatestVerification(null);

    try {
      const full = await trpc.speechPoint.syncBatches.get.query({ id: String(record._id) });
      setDetail(full);
    } catch (error: any) {
      message.error(error?.message || "加载批次详情失败");
    }

    if (!record.latestVerificationId) return;

    setVerificationLoading(true);
    try {
      const verification = await trpc.speechPoint.verifications.get.query({
        id: String(record.latestVerificationId),
      });
      setLatestVerification(verification);
    } catch (error: any) {
      message.error(error?.message || "加载核验记录失败");
    } finally {
      setVerificationLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns: ColumnsType<AnyRecord> = [
    { title: "批次 ID", dataIndex: "_id", width: 220, ellipsis: true, render: (v) => <Text copyable>{v}</Text> },
    { title: "状态", dataIndex: "status", width: 100, render: batchStatusTag },
    { title: "用户数", dataIndex: "itemCount", width: 80 },
    { title: "总积分", dataIndex: "totalPoints", width: 90 },
    { title: "开始", dataIndex: "startedAt", width: 170, render: formatTime },
    { title: "完成", dataIndex: "completedAt", width: 170, render: formatTime },
    {
      title: "最近核验",
      dataIndex: "verificationCheckedAt",
      width: 170,
      render: formatTime,
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => void openDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
        刷新
      </Button>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={items}
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setLimit(ps);
          },
        }}
      />
      <Modal
        title="批次详情"
        open={!!detail}
        onCancel={() => {
          setDetail(null);
          setLatestVerification(null);
        }}
        footer={null}
        width={900}
        destroyOnHidden
      >
        {detail && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="状态">{batchStatusTag(detail.status)}</Descriptions.Item>
              <Descriptions.Item label="错误">{detail.errorMessage || "-"}</Descriptions.Item>
              <Descriptions.Item label="最近核验时间">{formatTime(detail.verificationCheckedAt)}</Descriptions.Item>
            </Descriptions>
            {detail.latestVerificationId && (
              <Card size="small" title="最近核验摘要" loading={verificationLoading}>
                {latestVerification ? (
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="判定结果">
                        {verificationOutcomeTag(latestVerification.outcome)}
                      </Descriptions.Item>
                      <Descriptions.Item label="抽样用户">
                        {latestVerification.sample?.taokeId || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="期望分数">
                        {latestVerification.sample?.expectedPoints ?? "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="执行动作">
                        {verificationActionLabel(latestVerification.action)}
                      </Descriptions.Item>
                      <Descriptions.Item label="结论">{latestVerification.conclusion}</Descriptions.Item>
                    </Descriptions>
                  </Space>
                ) : (
                  !verificationLoading && <Text type="secondary">暂无核验记录</Text>
                )}
              </Card>
            )}
            <div>
              <Text strong>请求 payload</Text>
              <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 200 }}>
                {JSON.stringify(detail.requestPayload, null, 2)}
              </pre>
            </div>
            <div>
              <Text strong>响应 payload</Text>
              <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 200 }}>
                {JSON.stringify(detail.responsePayload, null, 2)}
              </pre>
            </div>
            {Array.isArray(detail.apiCalls) && detail.apiCalls.length > 0
              ? detail.apiCalls.map((call: AnyRecord, index: number) => (
                  <Card
                    key={String(call._id || index)}
                    size="small"
                    title={`超淘请求 #${(call.callIndex ?? index) + 1}（${call.points} 分，${call.taokeIds?.length || 0} 人，${call.apiSuccess ? "API成功·已入账" : `API失败·复查${call.verifyStatus || "-"}`}）`}
                  >
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      <div>
                        <Text strong>请求</Text>
                        <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 160, marginTop: 8 }}>
                          {JSON.stringify(call.request, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <Text strong>响应</Text>
                        <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 200, marginTop: 8 }}>
                          {JSON.stringify(call.response, null, 2)}
                        </pre>
                      </div>
                    </Space>
                  </Card>
                ))
              : Array.isArray(detail.chaotaoApiCalls) && detail.chaotaoApiCalls.length > 0
              ? detail.chaotaoApiCalls.map((call: AnyRecord, index: number) => (
                <Card
                  key={index}
                  size="small"
                  title={`超淘 API 调用 #${index + 1} (${call.success ? "成功" : "失败"})`}
                >
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Text type="secondary">
                      会员: {call.taokeIds?.length || 0} 人，{call.points} 分/人
                    </Text>
                    <div>
                      <Text strong>请求</Text>
                      <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 160, marginTop: 8 }}>
                        {JSON.stringify(call.request, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <Text strong>响应</Text>
                      <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 200, marginTop: 8 }}>
                        {JSON.stringify(call.response, null, 2)}
                      </pre>
                    </div>
                  </Space>
                </Card>
              ))
              : (
              <Alert type="info" showIcon message="该批次未记录超淘原始请求/响应（历史批次在功能上线前产生）" />
            )}
          </Space>
        )}
      </Modal>
    </Space>
  );
}

function VerificationsTab() {
  const { message } = App.useApp();
  const [items, setItems] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [outcome, setOutcome] = useState<string>("all");
  const [detail, setDetail] = useState<AnyRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await trpc.speechPoint.verifications.list.query({
        page,
        limit,
        outcome: outcome === "all" ? undefined : (outcome as any),
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      message.error(error?.message || "加载核验记录失败");
    } finally {
      setLoading(false);
    }
  }, [page, limit, outcome, message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openDetail = async (record: AnyRecord) => {
    setDetailLoading(true);
    try {
      const full = await trpc.speechPoint.verifications.get.query({ id: String(record._id) });
      setDetail(full);
    } catch (error: any) {
      message.error(error?.message || "加载核验详情失败");
    } finally {
      setDetailLoading(false);
    }
  };

  const rowAnalysisColumns: ColumnsType<AnyRecord> = [
    { title: "log_id", dataIndex: "logId", width: 100 },
    { title: "分数", dataIndex: "score", width: 70 },
    { title: "原因", dataIndex: "remark", width: 140, ellipsis: true },
    { title: "时间", dataIndex: "createTime", width: 170 },
    {
      title: "命中",
      dataIndex: "matched",
      width: 70,
      render: (v) => (v ? <Tag color="success">是</Tag> : <Tag>否</Tag>),
    },
    {
      title: "未通过原因",
      dataIndex: "rejectReasons",
      render: (reasons: string[] | undefined) =>
        reasons && reasons.length > 0 ? reasons.join("；") : "-",
    },
  ];

  const columns: ColumnsType<AnyRecord> = [
    { title: "核验时间", dataIndex: "verifiedAt", width: 170, render: formatTime },
    { title: "批次 ID", dataIndex: "batchId", width: 220, ellipsis: true, render: (v) => <Text copyable>{v}</Text> },
    { title: "结果", dataIndex: "outcome", width: 110, render: verificationOutcomeTag },
    { title: "抽样用户", dataIndex: ["sample", "taokeId"], width: 120 },
    { title: "期望分数", dataIndex: ["sample", "expectedPoints"], width: 90 },
    {
      title: "结论",
      dataIndex: "conclusion",
      ellipsis: true,
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => void openDetail(record)} loading={detailLoading}>
          证据
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space wrap>
        <Select
          value={outcome}
          onChange={setOutcome}
          style={{ width: 160 }}
          options={[
            { label: "全部结果", value: "all" },
            { label: "确认成功", value: "confirmed" },
            { label: "确认失败", value: "rejected" },
            { label: "已跳过", value: "skipped" },
            { label: "查询异常", value: "error" },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
          刷新
        </Button>
      </Space>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={items}
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setLimit(ps);
          },
        }}
      />
      <Modal
        title="核验证据详情"
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={960}
        destroyOnHidden
      >
        {detail && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="判定结果">{verificationOutcomeTag(detail.outcome)}</Descriptions.Item>
              <Descriptions.Item label="执行动作">{verificationActionLabel(detail.action)}</Descriptions.Item>
              <Descriptions.Item label="批次 ID" span={2}>
                <Text copyable>{String(detail.batchId)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="抽样用户">{detail.sample?.taokeId || "-"}</Descriptions.Item>
              <Descriptions.Item label="期望分数">{detail.sample?.expectedPoints ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="候选池大小">{detail.sample?.candidateCount ?? 0}</Descriptions.Item>
              <Descriptions.Item label="命中 log_id">{detail.matchedLogId ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="发放时间" span={2}>
                {formatTime(detail.criteria?.batchStartedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="时间窗口" span={2}>
                {formatTime(detail.criteria?.timeWindowStart)} ~ {formatTime(detail.criteria?.timeWindowEnd)}
              </Descriptions.Item>
              <Descriptions.Item label="匹配规则" span={2}>
                {(detail.criteria?.matchRules || []).join("；") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="结论" span={2}>
                {detail.conclusion}
              </Descriptions.Item>
            </Descriptions>

            {detail.sample?.candidateTaokeIds?.length > 0 && (
              <div>
                <Text strong>候选用户列表</Text>
                <div style={{ marginTop: 8 }}>
                  {detail.sample.candidateTaokeIds.map((id: string) => (
                    <Tag key={id} style={{ marginBottom: 4 }}>
                      {id}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Text strong>逐条比对分析</Text>
              <Table
                style={{ marginTop: 8 }}
                rowKey={(row) => String(row.logId)}
                size="small"
                columns={rowAnalysisColumns}
                dataSource={detail.rowAnalysis || []}
                pagination={false}
                scroll={{ x: 900 }}
              />
            </div>

            {detail.apiError && (
              <Alert type="error" showIcon message="查询异常" description={detail.apiError} />
            )}

            {detail.apiRequest && (
              <div>
                <Text strong>查分明细请求</Text>
                <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 160, marginTop: 8 }}>
                  {JSON.stringify(detail.apiRequest, null, 2)}
                </pre>
              </div>
            )}

            {detail.apiResponse && (
              <div>
                <Text strong>超淘原始响应</Text>
                <pre style={{ background: "#f8fafc", padding: 12, borderRadius: 8, overflow: "auto", maxHeight: 280, marginTop: 8 }}>
                  {JSON.stringify(detail.apiResponse, null, 2)}
                </pre>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </Space>
  );
}

export function SpeechPointManagement() {
  const { message, modal } = App.useApp();
  const [remoteSyncEnabled, setRemoteSyncEnabled] = useState(false);
  const [productionSystemConfigured, setProductionSystemConfigured] = useState(false);
  const [guziHubConfigured, setGuziHubConfigured] = useState(false);
  const [chaotaoConfigured, setChaotaoConfigured] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configForm] = Form.useForm<{ archiveSourceId?: string; chaotaoApiKey?: string }>();
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configModalSaving, setConfigModalSaving] = useState(false);
  const [archiveSources, setArchiveSources] = useState<Array<{ id: string; name: string; corpId: string }>>(
    []
  );
  const [hubPreview, setHubPreview] = useState<{
    archiveSourceName: string;
    protocolScorpid: string;
    guziHubId: string;
    guziHubName: string;
  } | null>(null);
  const [hubPreviewLoading, setHubPreviewLoading] = useState(false);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const config = await trpc.speechPoint.syncConfig.get.query();
      setRemoteSyncEnabled(config.remoteSyncEnabled);
      setProductionSystemConfigured(config.productionSystemConfigured);
      setGuziHubConfigured(config.guziHubConfigured);
      setChaotaoConfigured(config.chaotaoConfigured);
    } catch (error: any) {
      message.error(error?.message || "加载同步配置失败");
    } finally {
      setConfigLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const previewHubByArchiveSource = useCallback(
    async (archiveSourceId?: string) => {
      if (!archiveSourceId) {
        setHubPreview(null);
        return;
      }
      setHubPreviewLoading(true);
      try {
        const preview = await trpc.speechPoint.syncConfig.previewHub.query({ archiveSourceId });
        setHubPreview(preview);
      } catch (error: any) {
        setHubPreview(null);
        message.error(error?.message || "解析主体枢纽失败");
      } finally {
        setHubPreviewLoading(false);
      }
    },
    [message]
  );

  const handleRemoteSyncToggle = (checked: boolean) => {
    if (checked) {
      if (!productionSystemConfigured) {
        modal.warning({
          title: "请先连接产出系统",
          content: "开启实际请求前，请先完成主体枢纽关联并填写超淘 API Key。",
          okText: "去配置",
          onOk: () => openConfigModal(),
        });
        return;
      }
      modal.confirm({
        title: "开启实际请求超淘",
        content: "开启后，下轮定时同步将向超淘发送真实加分请求。请确认产出系统连接已正确配置。",
        okText: "确认开启",
        cancelText: "取消",
        onOk: async () => {
          setConfigSaving(true);
          try {
            const config = await trpc.speechPoint.syncConfig.set.mutate({ enabled: true });
            setRemoteSyncEnabled(config.remoteSyncEnabled);
            message.success("已开启实际请求超淘");
          } catch (error: any) {
            message.error(error?.message || "更新配置失败");
          } finally {
            setConfigSaving(false);
          }
        },
      });
      return;
    }

    void (async () => {
      setConfigSaving(true);
      try {
        const config = await trpc.speechPoint.syncConfig.set.mutate({ enabled: false });
        setRemoteSyncEnabled(config.remoteSyncEnabled);
        message.success("已关闭实际请求，新明细将挂起待发");
      } catch (error: any) {
        message.error(error?.message || "更新配置失败");
      } finally {
        setConfigSaving(false);
      }
    })();
  };

  const openConfigModal = async () => {
    setConfigModalOpen(true);
    setHubPreview(null);
    try {
      const [config, sources] = await Promise.all([
        trpc.speechPoint.syncConfig.get.query(),
        trpc.speechPoint.syncConfig.listArchiveSources.query(),
      ]);
      setArchiveSources(
        sources.map((source: AnyRecord) => ({
          id: String(source.id || source._id),
          name: String(source.name || ""),
          corpId: String(source.corpId || ""),
        }))
      );
      configForm.setFieldsValue({
        archiveSourceId: config.archiveSourceId || undefined,
        chaotaoApiKey: "",
      });
      if (config.guziHubConfigured && config.archiveSourceId) {
        setHubPreview({
          archiveSourceName: config.archiveSourceName,
          protocolScorpid: config.protocolScorpid,
          guziHubId: config.guziHubId,
          guziHubName: config.guziHubName,
        });
      }
    } catch (error: any) {
      message.error(error?.message || "加载配置失败");
    }
  };

  const handleSaveConfig = async () => {
    const values = await configForm.validateFields();
    if (!values.archiveSourceId && !guziHubConfigured) {
      message.warning("请选择存档源以关联主体枢纽");
      return;
    }
    setConfigModalSaving(true);
    try {
      const config = await trpc.speechPoint.syncConfig.set.mutate({
        enabled: remoteSyncEnabled,
        archiveSourceId: values.archiveSourceId,
        ...(values.chaotaoApiKey ? { chaotaoApiKey: values.chaotaoApiKey } : {}),
      });
      setRemoteSyncEnabled(config.remoteSyncEnabled);
      setProductionSystemConfigured(config.productionSystemConfigured);
      setGuziHubConfigured(config.guziHubConfigured);
      setChaotaoConfigured(config.chaotaoConfigured);
      setConfigModalOpen(false);
      message.success("产出系统连接已保存");
    } catch (error: any) {
      message.error(error?.message || "保存配置失败");
    } finally {
      setConfigModalSaving(false);
    }
  };

  return (
    <div className="fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="page-title-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            <ThunderboltOutlined style={{ marginRight: 8, color: "#0f766e" }} />
            发言积分
          </Title>
          <Text type="secondary">
            非白名单用户发言计分（文字/语音 10 分、图片 20 分、每日上限 50 分）。默认不向超淘发请求，开启后下轮同步才会实际加分。
          </Text>
        </div>
        <Space align="center" style={{ flexShrink: 0 }}>
          <Button size="small" onClick={openConfigModal}>
            连接产出系统
          </Button>
          {productionSystemConfigured ? (
            <Tag color="success">产出系统已连接</Tag>
          ) : (
            <Tag color="warning">产出系统未连接</Tag>
          )}
          <Text>实际请求超淘</Text>
          <Switch
            checked={remoteSyncEnabled}
            loading={configLoading || configSaving}
            onChange={handleRemoteSyncToggle}
          />
          {remoteSyncEnabled ? (
            <Tag color="success">实际请求中</Tag>
          ) : (
            <Tag>仅挂起</Tag>
          )}
        </Space>
      </div>

      <Card variant="borderless" className="shadow-sm">
        <Tabs
          items={[
            { key: "accruals", label: "加分明细", children: <AccrualsTab /> },
            { key: "reconcile", label: "核验补分", children: <ReconcileTab /> },
            { key: "batches", label: "同步批次", children: <SyncBatchesTab /> },
            { key: "verifications", label: "核验记录", children: <VerificationsTab /> },
            { key: "test", label: "测试录入", children: <TestAddPointsTab /> },
          ]}
        />
      </Card>

      <Modal
        title="连接产出系统"
        open={configModalOpen}
        onCancel={() => setConfigModalOpen(false)}
        onOk={handleSaveConfig}
        confirmLoading={configModalSaving}
        destroyOnHidden
        okText="保存"
        width={560}
      >
        <Form form={configForm} layout="vertical">
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            1. 关联主体枢纽
          </Typography.Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            选择存档源后，系统会按 corpId 查询 qw_subject_hub（protocol_scorpid），并保存 hubId 用于成员隔离。
          </Text>
          <Form.Item
            name="archiveSourceId"
            label="存档源"
            rules={[{ required: !guziHubConfigured, message: "请选择存档源" }]}
          >
            <Select
              placeholder="选择存档源"
              allowClear
              showSearch
              optionFilterProp="label"
              loading={hubPreviewLoading}
              options={archiveSources.map((source) => ({
                value: source.id,
                label: `${source.name} (${source.corpId})`,
              }))}
              onChange={(value) => {
                void previewHubByArchiveSource(typeof value === "string" ? value : undefined);
              }}
            />
          </Form.Item>
          {hubPreview && (
            <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="主体枢纽">{hubPreview.guziHubName || "-"}</Descriptions.Item>
              <Descriptions.Item label="hubId">{hubPreview.guziHubId}</Descriptions.Item>
              <Descriptions.Item label="protocol_scorpid">{hubPreview.protocolScorpid}</Descriptions.Item>
            </Descriptions>
          )}

          <Divider style={{ margin: "8px 0 16px" }} />

          <Typography.Title level={5}>2. 填写超淘 API Key</Typography.Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            Base URL 使用服务端环境变量；此处只需填写当前租户的超淘 API Key。
          </Text>
          <Form.Item
            name="chaotaoApiKey"
            label="超淘 API Key"
            rules={[{ required: !chaotaoConfigured, message: "请填写超淘 API Key" }]}
            extra={chaotaoConfigured ? "已配置，留空则不修改" : "即 x-api-key 的值"}
          >
            <Input.Password placeholder={chaotaoConfigured ? "留空则不修改" : "请输入 API Key"} allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
